import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ShieldCheck, KeyRound, Power } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const ROLES = [
  { key: "super_admin", label: "Super Admin", desc: "Full control of every module + user management" },
  { key: "editor", label: "Editor", desc: "Create / edit / delete in every module. No user management." },
  { key: "viewer", label: "Viewer", desc: "Read-only access to every module." },
  { key: "module_manager", label: "Module Manager", desc: "Read / write only to the modules you assign below." },
  { key: "infrastructure", label: "Infrastructure", desc: "Read-only access to all modules; write access on Immovable Assets. Suited for facilities & infrastructure managers." },
  { key: "vehicle_admin", label: "Vehicle Maintenance Admin", desc: "Manages Vehicle Master and Drivers only. No other trust modules." },
  { key: "institute_admin", label: "Institute Admin", desc: "Manages a specific institute — staff, departments, intake & fees." },
  { key: "institute_staff", label: "Institute Staff", desc: "Records intake & fees for a specific institute." },
];

const INSTITUTE_ROLES = ["institute_admin", "institute_staff"];

const MODULE_LABELS = {
  trust: "Trust Details",
  institutes: "Institutes",
  executive_body: "Executive Body",
  general_body: "General Body",
  banks: "Bank Accounts",
  documents: "Documents",
  immovables: "Immovable Assets",
  financials: "Financials",
  gallery: "Gallery",
  employees: "Employees",
  masters: "Masters",
};

const empty = {
  username: "",
  email: "",
  name: "",
  role: "viewer",
  password: "",
  allowed_modules: [],
  write_modules: [],
  institute_id: "",
  is_active: true,
};

const RolePill = ({ role }) => {
  const map = {
    super_admin: "bg-[#234333] text-white border-[#234333]",
    editor: "bg-[#3a5a40]/10 text-[#3a5a40] border-[#3a5a40]/30",
    viewer: "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/30",
    module_manager: "bg-[#d4af37]/10 text-[#8a6d1d] border-[#d4af37]/40",
    infrastructure: "bg-[#0D5C63]/10 text-[#0D5C63] border-[#0D5C63]/40",
    vehicle_admin: "bg-[#8B5A2B]/10 text-[#8B5A2B] border-[#8B5A2B]/40",
    institute_admin: "bg-[#3a5a40]/15 text-[#234333] border-[#234333]/40",
    institute_staff: "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/40",
  };
  const label =
    ROLES.find((r) => r.key === role)?.label || role || "—";
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
        map[role] || ""
      }`}
    >
      {label}
    </span>
  );
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetPwFor, setResetPwFor] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const fetchAll = async () => {
    const [a, m, i] = await Promise.all([
      api.get("/users"),
      api.get("/users/modules"),
      api.get("/institutes"),
    ]);
    setItems(a.data);
    setModules(m.data.modules || []);
    setInstitutes(i.data || []);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      ...empty,
      ...u,
      password: "",
      allowed_modules: u.allowed_modules || [],
      write_modules: u.write_modules || [],
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleModule = (mod, kind) => {
    setForm((f) => {
      const cur = new Set(f[kind]);
      if (cur.has(mod)) cur.delete(mod);
      else cur.add(mod);
      const next = { ...f, [kind]: Array.from(cur) };
      // write_modules must be subset of allowed_modules
      if (kind === "allowed_modules") {
        next.write_modules = next.write_modules.filter((m) => cur.has(m));
      }
      return next;
    });
  };

  const save = async () => {
    if (!editing && !form.password) {
      toast.error("Password is required for new users");
      return;
    }
    if (!form.username && !form.email) {
      toast.error("Username or email is required");
      return;
    }
    if (form.role === "module_manager" && form.allowed_modules.length === 0) {
      toast.error("Module Manager requires at least one allowed module");
      return;
    }
    if (INSTITUTE_ROLES.includes(form.role) && !form.institute_id) {
      toast.error("Institute is required for Institute Admin / Staff roles");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload = {
          email: form.email,
          name: form.name,
          role: form.role,
          allowed_modules: form.role === "module_manager" ? form.allowed_modules : [],
          write_modules: form.role === "module_manager" ? form.write_modules : [],
          institute_id: INSTITUTE_ROLES.includes(form.role) ? form.institute_id : "",
          is_active: form.is_active,
        };
        const { data } = await api.put(`/users/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("User updated");
      } else {
        const payload = {
          username: form.username || form.email,
          email: form.email,
          name: form.name,
          role: form.role,
          password: form.password,
          allowed_modules: form.role === "module_manager" ? form.allowed_modules : [],
          write_modules: form.role === "module_manager" ? form.write_modules : [],
          institute_id: INSTITUTE_ROLES.includes(form.role) ? form.institute_id : "",
          is_active: form.is_active,
        };
        const { data } = await api.post(`/users`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("User created");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const u = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/users/${u.id}`);
      setItems((arr) => arr.filter((x) => x.id !== u.id));
      toast.success("User removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleActive = async (u) => {
    try {
      const { data } = await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      toast.success(data.is_active ? "User enabled" : "User disabled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    }
  };

  const doResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/users/${resetPwFor.id}/reset-password`, { new_password: newPassword });
      toast.success("Password reset for " + (resetPwFor.username || resetPwFor.email));
      setResetPwFor(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Reset failed");
    }
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        // Super admins first, then by created_at desc
        if (a.role === "super_admin" && b.role !== "super_admin") return -1;
        if (b.role === "super_admin" && a.role !== "super_admin") return 1;
        return (b.created_at || "").localeCompare(a.created_at || "");
      }),
    [items],
  );

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Access Control</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Users & Roles</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Create accounts, assign roles, and grant per-module access. Only Super Admins can manage users.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="user-add-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add User
        </button>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Name", "Login", "Role", "Modules", "Source", "Active", "Last login", ""].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  data-testid={`user-row-${u.id}`}
                >
                  <td className="px-4 py-4 text-[#1c1a17]">{u.name || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">
                    <div className="font-mono text-[12px]">{u.username}</div>
                    {u.email ? <div className="text-[11px]">{u.email}</div> : null}
                  </td>
                  <td className="px-4 py-4">
                    <RolePill role={u.role} />
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">
                    {u.role === "module_manager" ? (
                      <div className="flex flex-wrap gap-1 max-w-[280px]">
                        {(u.allowed_modules || []).map((m) => {
                          const w = (u.write_modules || []).includes(m);
                          return (
                            <span
                              key={m}
                              className={`px-1.5 py-0.5 text-[10px] rounded-sm border ${
                                w
                                  ? "border-[#234333]/40 text-[#234333] bg-[#234333]/5"
                                  : "border-[#6b655b]/30 text-[#6b655b] bg-[#f9f8f6]"
                              }`}
                              title={w ? "read & write" : "read-only"}
                            >
                              {MODULE_LABELS[m] || m}
                              {w ? " ✏" : ""}
                            </span>
                          );
                        })}
                      </div>
                    ) : INSTITUTE_ROLES.includes(u.role) ? (
                      <span className="text-xs text-[#234333]">
                        {u.institute_name || "—"}
                      </span>
                    ) : (
                      <span className="text-xs text-[#6b655b]">All modules</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b] text-xs uppercase">{u.source || "manual"}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        u.is_active ? "bg-[#3a5a40]" : "bg-[#9e2a2b]"
                      }`}
                    />
                    <span className="ml-2 text-xs text-[#6b655b]">
                      {u.is_active ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-[#6b655b] text-xs whitespace-nowrap">
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openEdit(u)}
                      data-testid={`user-edit-${u.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> Edit
                    </button>
                    <button
                      onClick={() => setResetPwFor(u)}
                      data-testid={`user-reset-${u.id}`}
                      className="ml-1 inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <KeyRound size={14} strokeWidth={1.5} /> Reset
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      data-testid={`user-toggle-${u.id}`}
                      disabled={u.username === me?.username}
                      className="ml-1 inline-flex items-center gap-1 text-[#6b655b] hover:bg-[#efece6] px-2 py-1 rounded-sm disabled:opacity-40"
                    >
                      <Power size={14} strokeWidth={1.5} />
                      {u.is_active ? "Disable" : "Enable"}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(u)}
                      data-testid={`user-delete-${u.id}`}
                      disabled={u.username === me?.username}
                      className="ml-1 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm disabled:opacity-40"
                    >
                      <Trash2 size={14} strokeWidth={1.5} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="user-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit User" : "Add User"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Full Name *">
              <input
                data-testid="user-name-input"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label={editing ? "Username (read-only)" : "Username (optional, falls back to email)"}>
              <input
                data-testid="user-username-input"
                className={inputCls}
                value={form.username}
                disabled={!!editing}
                onChange={(e) => set("username", e.target.value)}
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="user-email-input"
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            {!editing && (
              <Field label="Initial Password *">
                <input
                  data-testid="user-password-input"
                  type="text"
                  className={inputCls + " font-mono"}
                  value={form.password}
                  onChange={(e) => set("password", e.target.value)}
                  placeholder="At least 6 characters"
                />
              </Field>
            )}
            <Field label="Role *" full>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ROLES.map((r) => (
                  <label
                    key={r.key}
                    data-testid={`user-role-option-${r.key}`}
                    className={`block border rounded-sm px-4 py-3 cursor-pointer ${
                      form.role === r.key
                        ? "border-[#234333] bg-[#234333]/5"
                        : "border-[#e5e0d8] hover:bg-[#f9f8f6]"
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      checked={form.role === r.key}
                      onChange={() => set("role", r.key)}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={14} strokeWidth={1.5} className="text-[#234333]" />
                      <div className="text-sm font-semibold text-[#1c1a17]">{r.label}</div>
                    </div>
                    <div className="text-[11px] text-[#6b655b] mt-1">{r.desc}</div>
                  </label>
                ))}
              </div>
            </Field>

            {INSTITUTE_ROLES.includes(form.role) && (
              <Field label="Institute *" full>
                <select
                  data-testid="user-institute-select"
                  className={inputCls}
                  value={form.institute_id}
                  onChange={(e) => set("institute_id", e.target.value)}
                >
                  <option value="">— Select institute —</option>
                  {institutes.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name} {i.code ? `(${i.code})` : ""}
                    </option>
                  ))}
                </select>
                <div className="text-[11px] text-[#6b655b] mt-2">
                  This user will only see and manage data for the selected institute via the
                  Institute Portal at <code className="text-[#234333]">/institute/login</code>.
                </div>
              </Field>
            )}

            {form.role === "module_manager" && (
              <Field label="Allowed Modules" full>
                <div
                  data-testid="user-modules-grid"
                  className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-[#e5e0d8] rounded-sm p-3 bg-white"
                >
                  {modules.map((m) => {
                    const allowed = form.allowed_modules.includes(m);
                    const writeable = form.write_modules.includes(m);
                    return (
                      <div
                        key={m}
                        className={`border rounded-sm p-2 ${
                          allowed ? "border-[#234333]/40 bg-[#f9f8f6]" : "border-[#e5e0d8]"
                        }`}
                      >
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`user-module-read-${m}`}
                            checked={allowed}
                            onChange={() => toggleModule(m, "allowed_modules")}
                            className="w-4 h-4 accent-[#234333]"
                          />
                          <span className="text-sm text-[#1c1a17]">{MODULE_LABELS[m] || m}</span>
                        </label>
                        <label
                          className={`flex items-center gap-2 mt-1.5 ml-6 cursor-pointer ${
                            !allowed ? "opacity-40 cursor-not-allowed" : ""
                          }`}
                        >
                          <input
                            type="checkbox"
                            data-testid={`user-module-write-${m}`}
                            checked={writeable}
                            disabled={!allowed}
                            onChange={() => toggleModule(m, "write_modules")}
                            className="w-3.5 h-3.5 accent-[#234333]"
                          />
                          <span className="text-[11px] text-[#6b655b]">Can edit</span>
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[11px] text-[#6b655b] mt-2">
                  Check "Can edit" to grant write access. Read-only modules will still be visible.
                </div>
              </Field>
            )}

            <Field label="Active" full>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="user-active-checkbox"
                  checked={form.is_active}
                  onChange={(e) => set("is_active", e.target.checked)}
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">
                  Account is enabled (can sign in)
                </span>
              </label>
            </Field>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="user-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="user-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetPwFor} onOpenChange={(v) => !v && (setResetPwFor(null), setNewPassword(""))}>
        <DialogContent className="bg-white border-[#e5e0d8] rounded-sm max-w-md" data-testid="user-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">Reset Password</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#6b655b] mb-4">
            Set a new password for{" "}
            <span className="font-mono text-[#1c1a17]">
              {resetPwFor?.username || resetPwFor?.email}
            </span>
            . The user will need this to sign in next time.
          </div>
          <input
            data-testid="user-reset-input"
            type="text"
            className={inputCls + " font-mono"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 6 characters"
          />
          <DialogFooter className="pt-4">
            <button
              onClick={() => {
                setResetPwFor(null);
                setNewPassword("");
              }}
              data-testid="user-reset-cancel"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={doResetPassword}
              data-testid="user-reset-confirm"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm"
            >
              Reset Password
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this user?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name || confirmDelete?.username} will lose all access to GS Mandal.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="user-delete-cancel"
              className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="user-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
